import api from "./apiService";

/**
 * Fetch all setlists accessible to the current user
 * @param {Object} options - Options for fetching setlists
 * @param {boolean} options.communityOnly - Only fetch community setlists (default: false)
 * @param {boolean} options.userOnly - Only fetch user setlists (default: false)
 * @param {number} options.communityId - Filter by community ID
 * @returns {Promise<Array>} List of setlists
 */
export const fetchSetlists = async (options = {}) => {
    try {
        // Build query parameters
        const params = new URLSearchParams();
        
        if (options.communityOnly !== undefined) {
            params.append('community_only', options.communityOnly);
        }
        
        if (options.userOnly !== undefined) {
            params.append('user_only', options.userOnly);
        }
        
        if (options.communityId) {
            params.append('community_id', options.communityId);
        }
        
        const queryString = params.toString() ? `?${params.toString()}` : '';
        const response = await api.get(`/setlists${queryString}`);
        
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Fetch a specific community's setlists
 * @param {number} communityId - The community ID
 * @returns {Promise<Array>} List of community setlists
 */
export const fetchCommunitySetlists = async (communityId) => {
    try {
        const response = await api.get(`/setlists/community/${communityId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Fetch a setlist by ID
 * @param {number} setlistId - The setlist ID
 * @returns {Promise<Object>} Setlist data
 */
export const fetchSetlistById = async (setlistId) => {
    try {
        const response = await api.get(`/setlists/${setlistId}`);
        
        // Ensure songs are sorted by order if available
        if (response.data && response.data.songs) {
            // If songs don't have order property, add it based on array position
            const songsWithOrder = response.data.songs.map((song, index) => {
                if (song.order === undefined) {
                    return { ...song, order: index };
                }
                return song;
            });
            
            // Sort by order field
            response.data.songs = songsWithOrder.sort((a, b) => a.order - b.order);
        }
        
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Create a new setlist
 * @param {Object} setlistData - The setlist data
 * @param {string} setlistData.name - The name of the setlist
 * @param {Array} setlistData.songs - Array of song objects with song_id, song_role, comment, and order
 * @param {string} setlistData.owner_type - The owner type ('user' or 'community')
 * @param {number} setlistData.owner_id - The owner ID (user ID or community ID)
 * @returns {Promise<Object>} Created setlist data
 */
export const createSetlist = async (setlistData) => {
    
    // Ensure songs have order property
    if (setlistData.songs) {
        setlistData.songs = setlistData.songs.map((song, index) => ({
            ...song,
            order: song.order !== undefined ? song.order : index
        }));
    }
    
    try {
        const response = await api.post("/setlists", setlistData);
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Update an existing setlist
 * @param {number} setlistId - The ID of the setlist to update
 * @param {Object} setlistData - The updated setlist data
 * @param {string} [setlistData.name] - The name of the setlist
 * @param {Array} [setlistData.songs] - Array of song objects with song_id, song_role, comment, and order
 * @returns {Promise<Object>} Updated setlist data
 */
export const updateSetlist = async (setlistId, setlistData) => {
    
    // Ensure songs have order property
    if (setlistData.songs) {
        setlistData.songs = setlistData.songs.map((song, index) => ({
            ...song,
            order: song.order !== undefined ? song.order : index
        }));
    }
    
    try {
        const response = await api.put(`/setlists/${setlistId}`, setlistData);
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Delete a setlist
 * @param {number} setlistId - The ID of the setlist to delete
 * @returns {Promise<Object>} Response message
 */
export const deleteSetlist = async (setlistId) => {
    try {
        await api.delete(`/setlists/${setlistId}`);
        return { message: "Setlist deleted successfully" };
    } catch (error) {
        throw error;
    }
};